import { Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import clsx from 'clsx';
import { ReplyIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const trailingButtonClass =
  'absolute -top-2 -right-2 flex items-center justify-center size-4 rounded-full bg-background border border-border-light shadow-sm hover:bg-accent';

interface ActionProps {
  ariaLabel: string;
  testId: string;
  onClick: () => void;
}

function stopAnd(onClick: () => void) {
  return (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onClick();
  };
}

/**
 * Re-attach a past attachment to the next message. Hover-revealed, matching the
 * file/project-context chips. Must render inside an AttachmentChip (its
 * TooltipProvider) so the tooltip resolves.
 */
export function AttachmentAddButton(props: ActionProps) {
  const { t } = useTranslation('chats');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={props.ariaLabel}
          data-testid={props.testId}
          onClick={stopAnd(props.onClick)}
          className={clsx(
            trailingButtonClass,
            'opacity-0 transition-opacity group-hover:opacity-100',
          )}
        >
          <ReplyIcon className="size-2.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{t('chats.attachmentChipActions.addToChat')}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Drop an attachment from the next message. Always visible (signals it is
 * currently attached). Must render inside an AttachmentChip's TooltipProvider.
 */
export function AttachmentRemoveButton(props: ActionProps) {
  const { t } = useTranslation('chats');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={props.ariaLabel}
          data-testid={props.testId}
          onClick={stopAnd(props.onClick)}
          className={clsx(trailingButtonClass, 'transition-colors')}
        >
          <XIcon className="size-2.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{t('chats.attachmentChipActions.removeFromNextMessage')}</TooltipContent>
    </Tooltip>
  );
}
